import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrderDetailDto, OrderRemoveOutcome } from '@crm/shared'
import { removeOrderPositions } from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'

function apiMessage(e: unknown): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
    ?.error?.message
  return msg ?? 'Не удалось убрать позиции из заказа'
}

interface Props {
  order: OrderDetailDto
  requestItemIds: string[]
  onClose: () => void
  onDone: () => void
}

/**
 * «Убрать из заказа» (29.09.2026, docs/features/order-editing.md). Исход для
 * позиции заявки выбирают здесь: «Отказ клиента» с причиной (по умолчанию,
 * заявка остаётся «в заказе») или возврат в «КП выставлено» (позицию можно
 * предложить снова, заявка переоткрывается). Строки убранных позиций уходят
 * из счетов — счёт, где их не осталось, отменяется.
 */
export function RemoveOrderPositionsModal({ order, requestItemIds, onClose, onDone }: Props) {
  const qc = useQueryClient()
  const [outcome, setOutcome] = useState<OrderRemoveOutcome>('REJECTED')
  const [reason, setReason] = useState('Клиент убрал позицию из заказа')
  const [error, setError] = useState<string | null>(null)

  const selected = order.positions.filter((p) => requestItemIds.includes(p.requestItemId))
  const all = selected.length >= order.positions.length
  // Строки счетов, которые затронет удаление (для предупреждения).
  const invoiced = order.invoices.filter(
    (inv) =>
      inv.status !== 'CANCELLED' &&
      inv.items.some((it) => it.requestItemId && requestItemIds.includes(it.requestItemId)),
  )

  const mutation = useMutation({
    mutationFn: () =>
      removeOrderPositions(order.id, {
        requestItemIds,
        outcome,
        reason: outcome === 'REJECTED' ? reason.trim() : null,
      }),
    onSuccess: (detail) => {
      qc.setQueryData(['orders', order.id], detail)
      void qc.invalidateQueries({ queryKey: ['orders'], exact: false, refetchType: 'none' })
      void qc.invalidateQueries({ queryKey: ['request-items-by-request'] })
      void qc.invalidateQueries({ queryKey: ['requests'] })
      toast.success(selected.length === 1 ? 'Позиция убрана из заказа' : 'Позиции убраны из заказа')
      onDone()
    },
    onError: (e) => setError(apiMessage(e)),
  })

  const reasonOk = outcome !== 'REJECTED' || reason.trim().length >= 3

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">Убрать из заказа</h3>
        <p className="mb-3 text-sm text-gray-600">
          {selected.length === 1 ? 'Позиция' : `Позиций: ${selected.length}`} —{' '}
          {selected
            .slice(0, 5)
            .map((p) => p.partNumber)
            .join(', ')}
          {selected.length > 5 ? ' и другие' : ''}
        </p>

        {all ? (
          <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Все позиции убрать нельзя. Чтобы заменить позицию, сначала добавьте новую, потом уберите
            старую. Если клиент отказался от заказа целиком — нажмите «Отказ» вверху заказа.
          </p>
        ) : (
          <>
            <fieldset className="mb-3 space-y-2 text-sm">
              <legend className="mb-1 font-medium text-gray-700">
                Что сделать с позицией в заявке
              </legend>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  className="mt-1"
                  checked={outcome === 'REJECTED'}
                  onChange={() => setOutcome('REJECTED')}
                />
                <span>
                  <b>Отказ клиента</b> — позиция закрывается с причиной, заявка остаётся «в заказе»
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  className="mt-1"
                  checked={outcome === 'BACK_TO_KP'}
                  onChange={() => setOutcome('BACK_TO_KP')}
                />
                <span>
                  <b>Вернуть в «КП выставлено»</b> — позицию можно предложить снова, заявка
                  переоткроется
                </span>
              </label>
            </fieldset>

            {outcome === 'REJECTED' && (
              <label className="mb-3 block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Причина отказа</span>
                <textarea
                  className="w-full rounded border border-gray-300 px-2 py-1.5"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
            )}

            {invoiced.length > 0 && (
              <p className="mb-3 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Строки уйдут из счетов ({invoiced.length}). Счёт, в котором не останется позиций,
                будет отменён. После изменения скачайте шаблон счёта заново и обновите счёт в УНФ.
              </p>
            )}
          </>
        )}

        {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Отмена
          </button>
          {!all && (
            <button
              className="rounded-lg bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
              onClick={() => {
                setError(null)
                mutation.mutate()
              }}
              disabled={mutation.isPending || !reasonOk}
            >
              {mutation.isPending ? 'Убираю…' : 'Убрать из заказа'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
